import {getAccessToken} from './auth.service';

/** SQ-419: one of the documents a person agrees to (the API's Catalog/legal-documents.json). */
export interface LegalDocumentDto {
  title: string;
  version: string;
  /** On this app's own origin (public/documents). */
  path: string;
}

export interface LegalDocumentsDto {
  terms: LegalDocumentDto;
  privacy: LegalDocumentDto;
}

/** What the checkbox agreed to: the versions it showed. */
export interface LegalAcceptDto {
  accepted: boolean;
  termsVersion: string;
  privacyVersion: string;
}

export interface LegalStatusDto {
  documents: LegalDocumentsDto;
  acceptanceRequired: boolean;
  lastAcceptedAt?: string | null;
}

/** The API's codes for a refusal about the documents. */
export const TERMS_OUTDATED = 'terms_outdated';

export class LegalRefusalError extends Error {
  constructor(message: string, readonly code?: string) {
    super(message);
    this.name = 'LegalRefusalError';
  }
}

const apiBase = () => process.env.REACT_APP_ACCUSITE_API_BASE_URL;

export const acceptanceOf = (documents: LegalDocumentsDto): LegalAcceptDto => ({
  accepted: true,
  termsVersion: documents.terms.version,
  privacyVersion: documents.privacy.version,
});

/** The current documents; needs no sign-in (the signup form shows them before any account exists). */
export const getLegalDocuments = async (): Promise<LegalDocumentsDto> => {
  const response = await fetch(`${apiBase()}/api/v1/legal/documents`);
  if (!response.ok) {
    throw new Error(`Could not load the Terms of Service (${response.status})`);
  }
  return await response.json() as LegalDocumentsDto;
};

export const getLegalStatus = async (): Promise<LegalStatusDto> => {
  const token = await getAccessToken();
  const response = await fetch(`${apiBase()}/api/v1/legal/status`, {
    headers: {Authorization: `Bearer ${token}`},
  });
  if (!response.ok) {
    throw new Error(`Could not load the Terms of Service (${response.status})`);
  }
  return await response.json() as LegalStatusDto;
};

/** POST legal/acceptances: an invited member's first sign-in, or anyone after a document changed. */
export const acceptLegalDocuments = async (dto: LegalAcceptDto): Promise<LegalStatusDto> => {
  const token = await getAccessToken();
  const response = await fetch(`${apiBase()}/api/v1/legal/acceptances`, {
    method: 'POST',
    headers: {Authorization: `Bearer ${token}`, 'Content-Type': 'application/json'},
    body: JSON.stringify(dto),
  });
  if (response.ok) {
    return await response.json() as LegalStatusDto;
  }
  const body = await response.json().catch(() => null) as {message?: string; code?: string} | null;
  throw new LegalRefusalError(body?.message ?? `Could not record your agreement (${response.status})`, body?.code);
};
